import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { label: "What we do", href: "#offerings" },
  { label: "About", href: "#about" },
  { label: "FAQs", href: "#faq" },
];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#f7f5f0]/85 backdrop-blur-xl border-b border-ink/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 lg:px-12">
        <div className="flex h-16 md:h-20 items-center justify-between">
          <a
            href="#top"
            data-testid="nav-logo"
            className="font-display font-black text-xl md:text-2xl tracking-tight text-ink flex items-center gap-1"
          >
            RCS Labs<span className="text-money">.</span>
          </a>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-mono text-[12px] uppercase tracking-[0.18em] text-ink/70 hover:text-ink transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-ink group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </nav>

          <a
            href="mailto:divyat@rcslabs.in"
            data-testid="nav-get-in-touch"
            className="hidden md:inline-flex items-center gap-2 bg-ink text-cream px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-acid hover:text-ink transition-all duration-300 group"
          >
            Get in Touch
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden text-ink p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden bg-cream border-t border-ink/10 overflow-hidden"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="font-display text-2xl font-bold text-ink py-3 border-b border-ink/10"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href="mailto:divyat@rcslabs.in"
                data-testid="mobile-get-in-touch"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-ink text-cream px-5 py-3.5 font-mono text-xs uppercase tracking-[0.18em]"
              >
                Get in Touch <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navigation;
